import api from '../utils/api';

/**
 * @typedef {Object} Campaign
 * @property {string} _id
 * @property {string} title
 * @property {string} description
 * @property {string} type - 'promotional', 'educational', 'announcement', 'newsletter', 'course_update', 'welcome', 'onboarding', 'abandoned_cart', 're_engagement', 'survey', 'reminder'
 * @property {string} status - 'draft', 'scheduled', 'sending', 'sent', 'cancelled'
 * @property {number} templateId - Brevo Template ID
 * @property {Object} templateParams - Dynamic template variables
 * @property {string} [subject] - Email subject line (optional, can override template)
 * @property {string} [preheader] - Email preheader text (optional, can override template)
 * @property {Object} [body] - Legacy support for manual HTML content
 * @property {string} [body.html] - HTML email content (legacy)
 * @property {string} [body.text] - Plain text email content (legacy)
 * @property {Object} targeting
 * @property {string} targeting.audienceType
 * @property {string[]} targeting.courses
 * @property {string[]} targeting.roles
 * @property {string[]} targeting.enrollmentStatus
 * @property {Object} targeting.lastActivityDays
 * @property {string[]} [targeting.specificStudents] - Specific user IDs for manual selection
 * @property {number} targeting.recipientsCount
 * @property {Object} schedule
 * @property {string} schedule.scheduledAt
 * @property {boolean} schedule.sendImmediately
 * @property {Object} analytics
 * @property {number} analytics.sent
 * @property {number} analytics.delivered
 * @property {number} analytics.opened
 * @property {number} analytics.clicked
 * @property {number} analytics.bounced
 * @property {number} analytics.unsubscribed
 * @property {Object} createdBy
 * @property {string} createdBy._id
 * @property {string} createdBy.firstName
 * @property {string} createdBy.lastName
 * @property {string} createdBy.email
 * @property {string} sentAt
 * @property {string} completedAt
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} CreateCampaignData
 * @property {string} title
 * @property {string} [description]
 * @property {string} type
 * @property {number} templateId - Brevo Template ID (required)
 * @property {Object} [templateParams] - Dynamic template variables
 * @property {string} [subject] - Optional email subject (can override template)
 * @property {string} [preheader] - Optional preheader (can override template)
 * @property {Object} [body] - Legacy support for manual HTML content
 * @property {string} [body.html] - HTML email content (legacy)
 * @property {string} [body.text] - Plain text email content (legacy)
 * @property {Object} targeting
 * @property {string} targeting.audienceType
 * @property {string[]} [targeting.courses]
 * @property {string[]} [targeting.roles]
 * @property {string[]} [targeting.enrollmentStatus]
 * @property {Object} [targeting.lastActivityDays]
 * @property {string[]} [targeting.specificStudents] - Specific user IDs for manual selection
 * @property {Object} schedule
 * @property {boolean} schedule.sendImmediately
 * @property {string} [schedule.scheduledAt]
 */

export const campaignService = {
  // ==================
  // CAMPAIGN MANAGEMENT
  // ==================

  /**
   * Create campaign
   * @param {CreateCampaignData} campaignData
   * @returns {Promise<{status: string, message: string, data: Campaign}>}
   */
  createCampaign: async (campaignData) => {
    const response = await api.post('/api/email-campaigns', campaignData);
    return response;
  },

  /**
   * Get all campaigns
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.status - Filter by status
   * @param {string} params.type - Filter by type
   * @param {string} params.sortBy - Sort field
   * @param {string} params.order - Sort order (asc/desc)
   * @returns {Promise<{status: string, count: number, total: number, pagination: Object, data: Campaign[]}>}
   */
  getAllCampaigns: async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.type) queryParams.append('type', params.type);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.order) queryParams.append('order', params.order);
    
    const queryString = queryParams.toString();
    const url = `/api/email-campaigns${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response;
  },

  /**
   * Get campaign by ID
   * @param {string} campaignId
   * @returns {Promise<{status: string, data: Campaign}>}
   */
  getCampaign: async (campaignId) => {
    const response = await api.get(`/api/email-campaigns/${campaignId}`);
    return response;
  },

  /**
   * Update campaign
   * @param {string} campaignId
   * @param {Object} updates
   * @returns {Promise<{status: string, message: string, data: Campaign}>}
   */
  updateCampaign: async (campaignId, updates) => {
    const response = await api.put(`/api/email-campaigns/${campaignId}`, updates);
    return response;
  },

  /**
   * Delete campaign
   * @param {string} campaignId
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteCampaign: async (campaignId) => {
    const response = await api.delete(`/api/email-campaigns/${campaignId}`);
    return response;
  },

  // ==================
  // CAMPAIGN ACTIONS
  // ==================

  /**
   * Calculate recipients based on targeting rules
   * @param {Object} targeting
   * @returns {Promise<{status: string, count: number, recipients: Object[]}>}
   */
  calculateRecipients: async (targeting) => {
    const response = await api.post('/api/email-campaigns/calculate-recipients', { targeting });
    return response;
  },

  /**
   * Send campaign immediately
   * @param {string} campaignId
   * @returns {Promise<{status: string, message: string, data: Object}>}
   */
  sendCampaign: async (campaignId) => {
    const response = await api.post(`/api/email-campaigns/${campaignId}/send`);
    return response;
  },

  /**
   * Schedule campaign
   * @param {string} campaignId
   * @param {string} scheduledAt ISO date string
   * @returns {Promise<{status: string, message: string, data: Campaign}>}
   */
  scheduleCampaign: async (campaignId, scheduledAt) => {
    const response = await api.post(`/api/email-campaigns/${campaignId}/schedule`, { scheduledAt });
    return response;
  },

  /**
   * Cancel scheduled campaign
   * @param {string} campaignId
   * @returns {Promise<{status: string, message: string}>}
   */
  cancelCampaign: async (campaignId) => {
    const response = await api.post(`/api/email-campaigns/${campaignId}/cancel`);
    return response;
  },

  /**
   * Get campaign analytics
   * @param {string} campaignId
   * @returns {Promise<{status: string, data: Object}>}
   */
  getCampaignAnalytics: async (campaignId) => {
    const response = await api.get(`/api/email-campaigns/${campaignId}/analytics`);
    return response;
  },
};
